import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import UsersTab from "@/pages/super-admin/components/UsersTab";
import SellersTab from "@/pages/super-admin/components/SellersTab";
import AdminsTab from "@/pages/super-admin/components/AdminsTab";
import AuditTab from "@/pages/super-admin/components/AuditTab";
import EmailsTab from "@/pages/super-admin/components/EmailsTab";
import type { AdminProfile } from "@/pages/admin/dashboard/types";

export interface Permission {
  id: string;
  name: string;
  description: string | null;
}

export interface RolePermission {
  role: string;
  permission_id: string;
}

export interface AuditLog {
  id: string;
  actor_user_id: string | null;
  action: string;
  target_user_id: string | null;
  target_resource: string | null;
  created_at: string;
  old_data: unknown;
  new_data: unknown;
}

type TabKey = "users" | "sellers" | "admins" | "audit" | "emails";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "users", label: "Usuarios", icon: "ri-team-line" },
  { key: "sellers", label: "Vendedores", icon: "ri-store-2-line" },
  { key: "admins", label: "Administradores", icon: "ri-shield-star-line" },
  { key: "audit", label: "Auditoría", icon: "ri-file-search-line" },
  { key: "emails", label: "Emails", icon: "ri-mail-send-line" },
];

export default function SuperAdminDashboard() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>("users");
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, permRes, rpRes, auditRes] = await Promise.all([
        supabase.from("profiles").select("*").order("created_at", { ascending: false }),
        supabase.from("admin_permissions").select("*").order("name"),
        supabase.from("admin_role_permissions").select("*"),
        supabase.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(100),
      ]);
      if (pRes.error) throw pRes.error;
      if (permRes.error) throw permRes.error;
      if (rpRes.error) throw rpRes.error;
      if (auditRes.error) throw auditRes.error;
      setProfiles((pRes.data ?? []) as AdminProfile[]);
      setPermissions((permRes.data ?? []) as Permission[]);
      setRolePermissions((rpRes.data ?? []) as RolePermission[]);
      setAuditLogs((auditRes.data ?? []) as AuditLog[]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudieron cargar los datos."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto px-4 py-8 md:px-6 lg:max-w-7xl">
        {/* Encabezado */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-vip-crown-line text-xl text-primary-500"></i>
            </span>
            <div>
              <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                Hola, {profile?.full_name ?? "Super Admin"}
                <span className="whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                  super_admin
                </span>
              </h1>
              <p className="text-sm text-foreground-600">
                Panel de control exclusivo del super administrador.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap self-start rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:ml-auto sm:self-auto"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Actualizar
          </button>
        </div>

        {/* Pestañas */}
        <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                activeTab === tab.key
                  ? "bg-primary-500 text-white"
                  : "text-foreground-500 hover:text-foreground-900"
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div className="mt-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-sm text-foreground-500">Cargando datos…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
                <i className="ri-error-warning-line text-2xl text-accent-700"></i>
              </span>
              <p className="mt-4 text-sm font-semibold text-foreground-900">
                Ocurrió un error
              </p>
              <p className="mt-1 max-w-sm text-sm text-foreground-500">{error}</p>
              <button
                type="button"
                onClick={fetchAll}
                className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Reintentar
              </button>
            </div>
          ) : activeTab === "users" ? (
            <UsersTab
              profiles={profiles}
              currentUserId={profile?.id}
              onRefresh={fetchAll}
            />
          ) : activeTab === "sellers" ? (
            <SellersTab profiles={profiles} onRefresh={fetchAll} />
          ) : activeTab === "admins" ? (
            <AdminsTab
              profiles={profiles}
              permissions={permissions}
              rolePermissions={rolePermissions}
              onRefresh={fetchAll}
            />
          ) : activeTab === "emails" ? (
            <EmailsTab />
          ) : (
            <AuditTab auditLogs={auditLogs} profiles={profiles} />
          )}
        </div>
      </main>
    </div>
  );
}